"use client"

/**
 * Theme switcher de la FAMILIA SaltaCompra (registry `@saltacompra/theme-switcher`,
 * habilitado por #437 / theme-core). Vendorizado tal cual lo emite el registry
 * (`registry/new-york/brand/theme-switcher.tsx`) para no depender de una fetch de
 * red en build/CI. Es un control PURO y CONTROLADO: en modo controlado
 * (`theme` + `onChange`) NO toca el DOM — la app (ThemeContext + next-themes)
 * gobierna el tema y el script anti-flash pre-hidratación por clase `.dark`.
 *
 * Regla de familia (Decisión 10 / ADR-0028): light por defecto, dark opt-in
 * persistido por usuario bajo la key `sc-theme`.
 */

import * as React from "react"
import { Moon, Sun } from "lucide-react"

import { cn } from "@/lib/utils"

export type Theme = "light" | "dark"
export type ThemeSwitcherVariant = "segmented" | "icon"

/**
 * Clave de la regla de familia SaltaCompra (Decisión 10): light por defecto,
 * dark opt-in persistido por usuario. La app debe correr un script inline
 * pre-hidratación que lea esta clave para evitar el flash de tema incorrecto.
 */
const STORAGE_KEY = "sc-theme"

function persistTheme(theme: Theme) {
  document.documentElement.classList.toggle("dark", theme === "dark")
  try {
    localStorage.setItem(STORAGE_KEY, theme)
  } catch {
    // localStorage no disponible (SSR/privacidad): el toggle sigue en memoria.
  }
}

export interface ThemeSwitcherProps {
  /** Controlado: refleja el valor y NO toca el DOM (la app gestiona el tema). */
  theme?: Theme
  /** No controlado: tema inicial. Default "light". */
  defaultTheme?: Theme
  /** Se dispara en cada cambio (ambos modos). */
  onChange?: (theme: Theme) => void
  /** "segmented" (pastilla Claro / Oscuro) o "icon" (toggle único). Default "segmented". */
  variant?: ThemeSwitcherVariant
  className?: string
}

/**
 * Switch de tema claro/oscuro. Light es el default de la familia; dark es opt-in.
 * Controlado (`theme` + `onChange`) o no controlado (togglea `.dark` en <html> y
 * persiste en `localStorage["sc-theme"]`). El foco es el anillo oro constante de
 * los tokens.
 */
function ThemeSwitcher({
  theme,
  defaultTheme = "light",
  onChange,
  variant = "segmented",
  className,
}: ThemeSwitcherProps) {
  const controlled = theme !== undefined
  const [internal, setInternal] = React.useState<Theme>(defaultTheme)
  const current: Theme = controlled ? (theme as Theme) : internal

  // En modo no controlado, sincronizar desde el DOM tras montar: el script
  // inline pre-hidratación de la app es la fuente de verdad del tema inicial.
  React.useEffect(() => {
    if (controlled) return
    setInternal(document.documentElement.classList.contains("dark") ? "dark" : "light")
  }, [controlled])

  const set = (t: Theme) => {
    if (!controlled) {
      setInternal(t)
      persistTheme(t)
    }
    onChange?.(t)
  }

  if (variant === "icon") {
    const next: Theme = current === "dark" ? "light" : "dark"
    return (
      <button
        type="button"
        aria-label={next === "dark" ? "Activar tema oscuro" : "Activar tema claro"}
        onClick={() => set(next)}
        className={cn(
          "inline-grid size-9 place-items-center rounded-sm border border-border bg-card text-foreground transition-colors hover:bg-secondary",
          className
        )}
      >
        {current === "dark" ? <Sun className="size-4" /> : <Moon className="size-4" />}
      </button>
    )
  }

  return (
    <div
      role="group"
      aria-label="Tema de la interfaz"
      className={cn(
        "inline-flex gap-[3px] rounded-md border border-border bg-muted p-[3px]",
        className
      )}
    >
      {(
        [
          { value: "light" as const, label: "Claro", icon: Sun },
          { value: "dark" as const, label: "Oscuro", icon: Moon },
        ]
      ).map(({ value, label, icon: Icon }) => {
        const on = current === value
        return (
          <button
            key={value}
            type="button"
            aria-pressed={on}
            onClick={() => set(value)}
            className={cn(
              "inline-flex h-[30px] items-center gap-1.5 rounded-sm px-3 text-xs font-semibold transition-colors",
              on
                ? "bg-card text-foreground shadow-xs"
                : "bg-transparent text-muted-foreground hover:text-foreground"
            )}
          >
            <Icon className="size-3.5" />
            {label}
          </button>
        )
      })}
    </div>
  )
}

export { ThemeSwitcher }
